import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import {
  KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, OPTION_ANALYSIS_FILTER, TASK_MANAGEMENT_PERMISSIONS,
} from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import { useLazyGetSectionsDetailsMAQuery, useUpdateSectionMAMutation } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import {
  useAnalysisAICreateMutation, useGetSingleMAInitiateQuery,
  useGetStakeholdersAnalysisListQuery, useStakeholderAnalysisUpdateMutation,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import { getNotificationErrorMessage } from 'utils/helpers';

import MaterialityConfigAnalysisView from './MaterialityConfigAnalysisView';

import type IAnalysisItem from 'store/slices/materialAssessment/interfaces/IAnalysisItem';
import type IStakeholderAnalysisItem from 'store/slices/materialAssessment/interfaces/IStakeholderAnalysisItem';
import type IOptionSelect from './interfaces/IOptionSelect';

function MaterialityConfigAnalysis() {
  const navigate = useNavigate();
  const { recordId = '', vcmId = 0 } = useParams();
  const [openModal, setOpenModal] = useState(false);
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<IStakeholderAnalysisItem[]>([]);
  const [selectedOption, setSelectedOption] = useState<IOptionSelect>(OPTION_ANALYSIS_FILTER[0]);
  const [updateSection] = useUpdateSectionMAMutation();
  const [selectedEditItemId, setSelectedEditItemId] = useState<number>(-1);
  const [observeStatus, setObserveStatus] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<number>(0);
  const [createAnalysisAI] = useAnalysisAICreateMutation();
  const [getSectionDetails] = useLazyGetSectionsDetailsMAQuery();
  const [updateStakeholderAnalysis] = useStakeholderAnalysisUpdateMutation();
  const hasAutoRefreshed = useRef(false);

  const {
    data: stakeholdersAnalysisList,
    isFetching: fetchingList,
    refetch: refetchList,
  } = useGetStakeholdersAnalysisListQuery({
    maId: recordId,
    vcmId: Number(vcmId),
    offset,
    ...(selectedOption.label !== 'All' ? { type: selectedOption.value } : {}),
  });

  const { meta, items: stakeholdersItems } = stakeholdersAnalysisList?.data || {};

  const hasMore = meta?.hasNext || false;

  const onLoadMore = () => {
    if (hasMore) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  const { data: userData, isLoading: isUserLoading } = useUserMeQuery();
  const { data: singleMA, isFetching: isLoadingSingleMA } = useGetSingleMAInitiateQuery({ id: recordId });
  const { data: locationsCountries, isFetching: isLocationsCountriesLoading } = useGetLocationsCountriesQuery();
  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const isValidStakeholderAnalysisItem = (item: IStakeholderAnalysisItem) => (
    item.stakeholder != null || item.vcmStakeholder != null
  );

  const mergeStakeholderAnalysisItems = (
    currentItems: IStakeholderAnalysisItem[],
    updatedItems: IStakeholderAnalysisItem[],
  ) => {
    const itemsById = new Map(currentItems.map((item) => [item.id, item]));

    updatedItems
      .filter(isValidStakeholderAnalysisItem)
      .forEach((item) => {
        itemsById.set(item.id, item);
      });

    return Array.from(itemsById.values());
  };

  const clearParams = () => {
    setOffset(0);
    setItems([]);
  };

  useEffect(() => {
    clearParams();
    refetchList();
  }, [selectedOption, refetchList]);

  useEffect(() => {
    if (stakeholdersItems) {
      setItems((prev) => mergeStakeholderAnalysisItems(prev, stakeholdersItems));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakeholdersItems]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    const checkSectionStatus = () => {
      if (recordId) {
        getSectionDetails({
          maId: recordId,
          type: SECTION_TYPE.TOPIC_1_STAKEHOLDER_INSIGHTS,
          vcmId: Number(vcmId),
        }).then((res) => {
          if ('data' in res && res.data) {
            if (res.data.data.status !== STATUS_SECTIONS.INTERNAL_PROCESSING) {
              setObserveStatus(false);
              refetchList();
              const metadataLastUpdate = res.data.data.metadata?.lastUpdate;
              setLastUpdate(metadataLastUpdate ?? Date.now());
              toast.success(i18n.t('notifications.updateInformationSuccess'));
            }
          } else {
            setObserveStatus(true);
          }
        });
      }
    };

    if (observeStatus && recordId) {
      intervalId = setInterval(checkSectionStatus, 10000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [observeStatus, getSectionDetails, recordId, refetchList, vcmId]);

  useEffect(() => {
    if (recordId) {
      getSectionDetails({
        maId: recordId,
        type: SECTION_TYPE.TOPIC_1_STAKEHOLDER_INSIGHTS,
        vcmId: Number(vcmId),
      }).then((res) => {
        if ('data' in res && res.data) {
          if (res.data.data.status !== STATUS_SECTIONS.INTERNAL_PROCESSING) {
            setObserveStatus(false);
            refetchList();
            const metadataLastUpdate = res.data.data.metadata?.lastUpdate;
            setLastUpdate(metadataLastUpdate ?? Date.now());
          }
        } else {
          setObserveStatus(true);
        }
      });
    }
  }, [recordId, vcmId, getSectionDetails, refetchList]);

  const goBack = () => {
    navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
  };

  const onSave = async () => {
    if (isViewOnly) {
      navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
      return;
    }

    try {
      await updateSection({
        maId: recordId,
        type: SECTION_TYPE.TOPIC_1_STAKEHOLDER_INSIGHTS,
        valueChainMapId: Number(vcmId),
        data: {
          isActive: true,
          status: items.length > 0 ? STATUS_SECTIONS.COMPLETED : STATUS_SECTIONS.EMPTY,
        },
      }).unwrap();
      navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
    } catch (error) {
      toast.error(getNotificationErrorMessage(error, i18n.t('notifications.errorUpdateRecord')));
    }
  };

  const onEditItem = (itemName: number) => {
    setSelectedEditItemId(itemName);
    setOpenModal(true);
  };

  const onRefreshInfo = useCallback(async () => {
    setObserveStatus(true);
    try {
      await createAnalysisAI({ maId: recordId, vcmId: Number(vcmId) }).unwrap();
    } catch (error) {
      setObserveStatus(false);
      toast.error(getNotificationErrorMessage(error, i18n.t('notifications.errorUpdateInformation')));
      refetchList();
    }
  }, [createAnalysisAI, recordId, refetchList, vcmId]);

  useEffect(() => {
    if (!recordId || hasAutoRefreshed.current) {
      return;
    }

    hasAutoRefreshed.current = true;
    onRefreshInfo();
  }, [onRefreshInfo, recordId]);

  const onSelectedOption = (option: IOptionSelect) => {
    setSelectedOption(option);
  };

  const closeModal = () => {
    setOpenModal(false);
    setSelectedEditItemId(-1);
  };

  const onUpdateItem = async (itemUpdate: IAnalysisItem) => {
    try {
      const { data } = await updateStakeholderAnalysis({
        maId: recordId,
        id: selectedEditItemId,
        data: itemUpdate,
        vcmId: Number(vcmId),
      }).unwrap();

      if (isValidStakeholderAnalysisItem(data)) {
        setItems((prev) => mergeStakeholderAnalysisItems(prev, [data]));
      }

      toast.success(i18n.t('notifications.updateRecordSuccess'));
    } catch (error) {
      toast.error(getNotificationErrorMessage(error, i18n.t('notifications.errorUpdateRecord')));
    }
  };

  const onSelectedItem = () => items.find((item) => item.id === selectedEditItemId);

  const loading = fetchingList || isUserLoading || isLoadingSingleMA || isLocationsCountriesLoading;

  return (
    <MaterialityConfigAnalysisView
      goBack={goBack}
      onSave={onSave}
      isEmptyList={items.length === 0}
      loading={loading}
      items={items}
      onLoadMore={onLoadMore}
      hasMore={hasMore}
      onEditItem={onEditItem}
      onRefreshInfo={onRefreshInfo}
      selectedOption={selectedOption}
      onSelectedOption={onSelectedOption}
      openModal={openModal}
      selectedItem={onSelectedItem()}
      closeModal={closeModal}
      onUpdateItem={onUpdateItem}
      loadingAI={observeStatus}
      lastUpdate={lastUpdate}
      locationsCountries={locationsCountries?.data || []}
    />
  );
}

export default MaterialityConfigAnalysis;
